/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    init() {
      // Leave the header cart icon as a normal link to /cart.
      // Add-to-cart actions will show a compact confirmation toast instead.
      this.ensureToastStyles();
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent() {
      this.showAddedToast();
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, quickAddDrawer ? 200 : 0);
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      if (openDrawer) {
        this.showAddedToast();
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    ensureToastStyles() {
      if (document.getElementById('amarra-cart-toast-styles')) return;

      const style = document.createElement('style');
      style.id = 'amarra-cart-toast-styles';
      style.textContent = `
        .amarra-cart-toast {
          --amarra-blue: #0d3b66;
          position: fixed;
          right: 20px;
          bottom: 20px;
          z-index: 9999;
          width: min(350px, calc(100vw - 32px));
          padding: 17px 17px 16px;
          overflow: hidden;
          border: 1px solid rgba(13, 59, 102, 0.18);
          border-radius: 14px;
          background: #fff;
          color: #111;
          box-shadow: 0 16px 38px rgba(13, 59, 102, 0.16);
          opacity: 0;
          transform: translateY(12px);
          pointer-events: none;
          transition: opacity 180ms ease, transform 180ms ease;
        }

        .amarra-cart-toast::before {
          content: "";
          position: absolute;
          inset: 0 0 auto;
          height: 4px;
          background: var(--amarra-blue);
        }

        .amarra-cart-toast.is-visible {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }

        .amarra-cart-toast__top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 14px;
        }

        .amarra-cart-toast__content {
          display: flex;
          align-items: flex-start;
          gap: 11px;
          min-width: 0;
        }

        .amarra-cart-toast__check {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          width: 28px;
          height: 28px;
          margin-top: 1px;
          border-radius: 50%;
          background: rgba(13, 59, 102, 0.10);
          color: var(--amarra-blue);
          font-size: 17px;
          font-weight: 800;
          line-height: 1;
        }

        .amarra-cart-toast__title {
          margin: 0;
          color: var(--amarra-blue);
          font-size: 16px;
          font-weight: 780;
          line-height: 1.25;
        }

        .amarra-cart-toast__message {
          margin: 4px 0 0;
          font-size: 12px;
          line-height: 1.4;
          color: rgba(17, 17, 17, 0.68);
        }

        .amarra-cart-toast__close {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          width: 28px;
          height: 28px;
          margin: -2px -3px 0 0;
          padding: 0;
          border: 0;
          background: transparent;
          color: rgba(17, 17, 17, 0.56);
          cursor: pointer;
          font-size: 22px;
          line-height: 1;
        }

        .amarra-cart-toast__link {
          display: inline-flex;
          align-items: center;
          margin: 13px 0 0 39px;
          color: var(--amarra-blue);
          font-size: 13px;
          font-weight: 780;
          text-decoration: none;
        }

        .amarra-cart-toast__link:hover {
          text-decoration: underline;
          text-underline-offset: 3px;
        }

        #cart-icon-bubble.is-amarra-bouncing .header__cart-count,
        #cart-icon.is-amarra-bouncing {
          animation: amarra-cart-bounce 520ms ease;
        }

        @keyframes amarra-cart-bounce {
          0%, 100% { transform: scale(1); }
          35% { transform: scale(1.16); }
          65% { transform: scale(0.96); }
        }

        @media (max-width: 749px) {
          .amarra-cart-toast {
            right: 12px;
            bottom: 12px;
            left: 12px;
            width: auto;
            padding: 16px 15px 15px;
          }

          .amarra-cart-toast__link {
            margin-left: 39px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .amarra-cart-toast,
          #cart-icon-bubble.is-amarra-bouncing .header__cart-count,
          #cart-icon.is-amarra-bouncing {
            transition: none;
            animation: none;
          }
        }
      `;

      document.head.appendChild(style);
    }

    getToast() {
      let toast = document.getElementById('amarra-cart-toast');
      if (toast) return toast;

      toast = document.createElement('div');
      toast.id = 'amarra-cart-toast';
      toast.className = 'amarra-cart-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML = `
        <div class="amarra-cart-toast__top">
          <div class="amarra-cart-toast__content">
            <span class="amarra-cart-toast__check" aria-hidden="true">✓</span>

            <div>
              <p class="amarra-cart-toast__title">Added to your cart</p>
              <p class="amarra-cart-toast__message">Your cart has been updated successfully.</p>
            </div>
          </div>

          <button
            type="button"
            class="amarra-cart-toast__close"
            aria-label="Close cart confirmation"
            data-amarra-toast-close
          >
            &times;
          </button>
        </div>

        <a class="amarra-cart-toast__link" href="/cart">
          Review cart &rarr;
        </a>
      `;

      toast.addEventListener('click', (evt) => {
        if (evt.target.closest('[data-amarra-toast-close]')) {
          this.hideAddedToast();
        }
      });

      document.body.appendChild(toast);
      return toast;
    }

    showAddedToast() {
      this.ensureToastStyles();

      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      if (quickAddDrawer && typeof quickAddDrawer.close === 'function') {
        quickAddDrawer.close();
      }

      const toast = this.getToast();

      if (this.toastTimer) {
        clearTimeout(this.toastTimer);
      }

      const cartIcon = document.getElementById('cart-icon');
      const cartBubble = document.getElementById('cart-icon-bubble');

      cartIcon?.classList.remove('is-amarra-bouncing');
      cartBubble?.classList.remove('is-amarra-bouncing');

      requestAnimationFrame(() => {
        toast.classList.add('is-visible');
        cartIcon?.classList.add('is-amarra-bouncing');
        cartBubble?.classList.add('is-amarra-bouncing');
      });

      window.setTimeout(() => {
        cartIcon?.classList.remove('is-amarra-bouncing');
        cartBubble?.classList.remove('is-amarra-bouncing');
      }, 600);

      this.toastTimer = window.setTimeout(() => {
        this.hideAddedToast();
      }, 5000);
    }

    hideAddedToast() {
      const toast = document.getElementById('amarra-cart-toast');
      if (!toast) return;

      toast.classList.remove('is-visible');

      if (this.toastTimer) {
        clearTimeout(this.toastTimer);
        this.toastTimer = null;
      }
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}